import { INTL_LOCALE, type Locale } from './locale';

/**
 * All user-visible numbers go through these helpers. Intl formatters are expensive to build,
 * so they are memoized per (locale, options).
 */
const cache = new Map<string, Intl.NumberFormat>();

function numberFormat(locale: Locale, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = cache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(INTL_LOCALE[locale], options);
    cache.set(key, fmt);
  }
  return fmt;
}

export interface MoneyFormatOptions {
  currency: string;
  /** Compact notation for axes and dense UI, e.g. €1.2M. */
  compact?: boolean;
  /** Show cents. Defaults to false: whole units are the norm for planning. */
  cents?: boolean;
  /** Always show a sign (for deltas). */
  signed?: boolean;
}

export function formatMoney(value: number, locale: Locale, options: MoneyFormatOptions): string {
  const digits = options.cents === true ? 2 : 0;
  return numberFormat(locale, {
    style: 'currency',
    currency: options.currency,
    notation: options.compact === true ? 'compact' : 'standard',
    minimumFractionDigits: options.compact === true ? 0 : digits,
    maximumFractionDigits: options.compact === true ? 1 : digits,
    signDisplay: options.signed === true ? 'exceptZero' : 'auto',
  }).format(value);
}

/** `rate` is a fraction: 0.031 → "3.1%". `minimumDigits` keeps trailing zeros ("9.0%"). */
export function formatPercent(
  rate: number,
  locale: Locale,
  fractionDigits = 1,
  minimumDigits = 0,
): string {
  return numberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: minimumDigits,
    maximumFractionDigits: fractionDigits,
  }).format(rate);
}

/**
 * A probability for display: whole percent, but never rounded to a misleading 0 % or 100 %
 * ("<1%", ">99%").
 */
export function formatProbability(p: number, locale: Locale): string {
  if (p > 0 && p < 0.01) return `<${formatPercent(0.01, locale, 0)}`;
  if (p < 1 && p > 0.99) return `>${formatPercent(0.99, locale, 0)}`;
  return formatPercent(p, locale, 0);
}

export function formatNumber(value: number, locale: Locale, fractionDigits = 0): string {
  return numberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

const dateCache = new Map<string, Intl.DateTimeFormat>();

function dateFormat(locale: Locale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = dateCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(INTL_LOCALE[locale], { timeZone: 'UTC', ...options });
    dateCache.set(key, fmt);
  }
  return fmt;
}

/** "Sept 2026" / "септември 2026 г." — month labels (dates are UTC month starts). */
export function formatMonthYear(
  date: Date,
  locale: Locale,
  month: 'short' | 'long' = 'short',
): string {
  return dateFormat(locale, { month, year: 'numeric' }).format(date);
}

/** "27 Sept 2026" — a calendar date given as ISO `YYYY-MM-DD`. */
export function formatDate(iso: string, locale: Locale): string {
  return dateFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

export function formatYear(date: Date, locale: Locale): string {
  return dateFormat(locale, { year: 'numeric' }).format(date);
}

/** Month names January…December for pickers. */
export function monthNames(locale: Locale, style: 'long' | 'short' = 'long'): string[] {
  const fmt = dateFormat(locale, { month: style });
  return Array.from({ length: 12 }, (_, m) => fmt.format(new Date(Date.UTC(2026, m, 1))));
}

/** "3 minutes ago" / "преди 3 минути". */
export function formatRelativeTime(then: number, now: number, locale: Locale): string {
  const rtf = new Intl.RelativeTimeFormat(INTL_LOCALE[locale], { numeric: 'auto' });
  const seconds = Math.round((then - now) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) return rtf.format(seconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 2_592_000) return rtf.format(Math.round(seconds / 86_400), 'day');
  return dateFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(then),
  );
}

/** Currency symbol for a locale, e.g. "€" / "лв." / "$". */
export function currencySymbol(currency: string, locale: Locale): string {
  const part = numberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  })
    .formatToParts(0)
    .find((p) => p.type === 'currency');
  return part?.value ?? currency;
}

const minus = (s: string): string => s.replace('-', '−');

/** The formatters charts, tables and reports share, for one locale and currency. */
export interface Formatters {
  money: (v: number) => string;
  compact: (v: number) => string;
  signed: (v: number) => string;
  percent: (v: number) => string;
  /** Returns (IRR) with one decimal: "8.7%", "9.0%". */
  rate: (v: number) => string;
  /** Money back per unit put in: "×2.04". */
  multiple: (v: number) => string;
  count: (v: number) => string;
  /** Probabilities: whole percent, with "<1%" / ">99%" at the ends. */
  probability: (v: number) => string;
  year: (d: Date) => string;
  month: (d: Date) => string;
  monthLong: (d: Date) => string;
}

/** Formatters for a locale and currency. Negative numbers use a true minus sign (−). */
export function makeFormatters(locale: Locale, currency: string): Formatters {
  return {
    money: (v) => minus(formatMoney(v, locale, { currency })),
    compact: (v) => minus(formatMoney(v, locale, { currency, compact: true })),
    signed: (v) => minus(formatMoney(v, locale, { currency, signed: true })),
    percent: (v) => minus(formatPercent(v, locale, 0)),
    rate: (v) => minus(formatPercent(v, locale, 1, 1)),
    multiple: (v) => `×${minus(formatNumber(v, locale, 2))}`,
    count: (v) => formatNumber(v, locale),
    probability: (v) => formatProbability(v, locale),
    year: (d) => formatYear(d, locale),
    month: (d) => formatMonthYear(d, locale),
    monthLong: (d) => formatMonthYear(d, locale, 'long'),
  };
}
